/**
 * Transaction State Derivation Utilities
 *
 * Provides functions to derive canonical transaction state from API boolean flags
 * and get display properties (color, severity, label) for UI rendering.
 */

import {
  TransactionState,
  TransactionStateType,
  PaidState,
  TransferredState,
  SettledState,
  CanceledState,
  FailedState,
} from '../models/transaction-state.model';

/**
 * Partial transaction interface matching the relevant fields from TransactionV2.
 * Using Partial to allow flexibility in what fields are provided.
 */
export interface TransactionFlags {
  date?: string | null;
  paid?: boolean;
  canceled?: boolean;
  canceledDate?: string | null;
  cancelRequested?: boolean;
  cancelRequestDate?: string | null;
  paidToEstablishment?: boolean;
  paidToEstablishmentDate?: string | null;
  paidToEstablishmentValue?: number;
  transferedToBranch?: boolean;
  transferedToBranchDate?: string | null;
  transferedToBranchValue?: number;
  returnCode?: string;
  returnMessage?: string;
}

/**
 * Derives the canonical transaction state from API boolean flags.
 *
 * Uses priority-based derivation where higher priority states take precedence:
 * 1. Canceled (highest) - if tx.canceled is true
 * 2. Settled - if tx.paidToEstablishment is true
 * 3. Transferred - if tx.transferedToBranch is true
 * 4. Paid - if tx.paid is true
 * 5. Failed (lowest) - default state (not paid = failed)
 *
 * @param tx - Transaction object with boolean flags
 * @returns The derived TransactionState
 */
export function deriveTransactionState(tx: TransactionFlags): TransactionState {
  // Priority 1: Canceled (highest priority - irreversible)
  if (tx.canceled) {
    return {
      type: 'Canceled',
      cancelDate: tx.canceledDate ? new Date(tx.canceledDate) : null,
      cancelRequested: tx.cancelRequested ?? false,
      wasPaid: tx.paid ?? false,
    } satisfies CanceledState;
  }

  // Priority 2: Settled (money transferred to establishment)
  if (tx.paidToEstablishment) {
    return {
      type: 'Settled',
      settledDate: tx.paidToEstablishmentDate ? new Date(tx.paidToEstablishmentDate) : null,
      value: tx.paidToEstablishmentValue ?? 0,
    } satisfies SettledState;
  }

  // Priority 3: Transferred (money transferred to branch)
  if (tx.transferedToBranch) {
    return {
      type: 'Transferred',
      transferDate: tx.transferedToBranchDate ? new Date(tx.transferedToBranchDate) : null,
      value: tx.transferedToBranchValue ?? 0,
    } satisfies TransferredState;
  }

  // Priority 4: Paid (payment successful)
  if (tx.paid) {
    return {
      type: 'Paid',
      paidDate: tx.date ? new Date(tx.date) : null,
      cancelRequested: tx.cancelRequested ?? false,
    } satisfies PaidState;
  }

  // Priority 5: Failed (not paid, not canceled = failed)
  return {
    type: 'Failed',
    reason: tx.returnMessage ?? 'Unknown error',
    returnCode: tx.returnCode ?? '',
  } satisfies FailedState;
}

/**
 * Color mapping for transaction states.
 * Colors follow the design specification from CONTEXT.md.
 */
const STATE_COLORS: Record<TransactionStateType, string> = {
  Paid: '#3B82F6', // blue
  Transferred: '#8B5CF6', // purple
  Settled: '#10B981', // green
  Canceled: '#6B7280', // gray
  Failed: '#EF4444', // red
};

/**
 * Gets the hex color code for a transaction state.
 *
 * @param state - The transaction state
 * @returns Hex color code (e.g., '#F59E0B')
 */
export function getStateColor(state: TransactionState): string {
  return STATE_COLORS[state.type];
}

/**
 * PrimeNG severity types for Tag component
 */
export type StateSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';

/**
 * Severity mapping for transaction states.
 * Maps to PrimeNG Tag severity values.
 */
const STATE_SEVERITIES: Record<TransactionStateType, StateSeverity> = {
  Paid: 'info',
  Transferred: 'info',
  Settled: 'success',
  Canceled: 'secondary',
  Failed: 'danger',
};

/**
 * Gets the PrimeNG severity for a transaction state.
 * Used with PrimeNG Tag component.
 *
 * @param state - The transaction state
 * @returns PrimeNG severity string
 */
export function getStateSeverity(state: TransactionState): StateSeverity {
  return STATE_SEVERITIES[state.type];
}

/**
 * Label mapping for transaction states.
 * Human-readable labels for display.
 */
const STATE_LABELS: Record<TransactionStateType, string> = {
  Paid: 'Paid',
  Transferred: 'Transferred',
  Settled: 'Settled',
  Canceled: 'Canceled',
  Failed: 'Failed',
};

/**
 * Gets the human-readable label for a transaction state.
 *
 * @param state - The transaction state
 * @returns Human-readable label
 */
export function getStateLabel(state: TransactionState): string {
  // Special case: Canceled transaction that was previously paid
  if (state.type === 'Canceled' && state.wasPaid) {
    return 'Paid/Canceled';
  }
  // Special case: Paid transaction with cancel requested
  if (state.type === 'Paid' && state.cancelRequested) {
    return 'Paid - Cancel Requested';
  }
  return STATE_LABELS[state.type];
}

/**
 * Helper function to ensure exhaustive handling of transaction states.
 * Use in default case of switch statements.
 *
 * @param state - Should be `never` if all cases are handled
 * @throws Error with message about unhandled state
 */
export function assertNever(state: never): never {
  throw new Error(`Unhandled transaction state: ${JSON.stringify(state)}`);
}
